import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import {
  SelectField,
  TextAreaField,
  TextField,
  ToggleSwitch,
} from "@/components/base/FormControls";
import ImageUploader from "@/components/base/ImageUploader";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Brand, Category, Product } from "@/types";

interface ProductFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  product: Product | null;
  categories: Category[];
  brands: Brand[];
}

interface ProductForm {
  name: string;
  sku: string;
  barcode: string;
  model: string;
  category_id: string;
  brand_id: string;
  image_url: string;
  internal_description: string;
  catalog_description: string;
  cost_price: string;
  price: string;
  wholesale_price: string;
  tax_rate: string;
  unit: string;
  min_stock: string;
  tire_size: string;
  compatibility: string;
  notes: string;
  show_in_catalog: boolean;
  offer_active: boolean;
  active: boolean;
}

const EMPTY: ProductForm = {
  name: "",
  sku: "",
  barcode: "",
  model: "",
  category_id: "",
  brand_id: "",
  image_url: "",
  internal_description: "",
  catalog_description: "",
  cost_price: "",
  price: "",
  wholesale_price: "",
  tax_rate: "18",
  unit: "Unidad",
  min_stock: "0",
  tire_size: "",
  compatibility: "",
  notes: "",
  show_in_catalog: false,
  offer_active: false,
  active: true,
};

function toNumber(value: string): number | null {
  const parsed = Number.parseFloat(value.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

export default function ProductFormModal({
  open,
  onClose,
  onSaved,
  product,
  categories,
  brands,
}: ProductFormModalProps) {
  const toast = useToast();
  const { profile, hasPermission } = useAuth();
  const canSeeCost = hasPermission("cost.view");
  const [form, setForm] = useState<ProductForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (product) {
      setForm({
        name: product.name,
        sku: product.sku ?? "",
        barcode: product.barcode ?? "",
        model: product.model ?? "",
        category_id: product.category_id ?? "",
        brand_id: product.brand_id ?? "",
        image_url: product.image_url ?? "",
        internal_description: product.internal_description ?? "",
        catalog_description: product.catalog_description ?? "",
        cost_price: product.cost_price != null ? String(product.cost_price) : "",
        price: product.price != null ? String(product.price) : "",
        wholesale_price: product.wholesale_price != null ? String(product.wholesale_price) : "",
        tax_rate: product.tax_rate != null ? String(product.tax_rate) : "18",
        unit: product.unit ?? "Unidad",
        min_stock: product.min_stock != null ? String(product.min_stock) : "0",
        tire_size: product.tire_size ?? "",
        compatibility: product.compatibility ?? "",
        notes: product.notes ?? "",
        show_in_catalog: Boolean(product.show_in_catalog),
        offer_active: Boolean(product.offer_active),
        active: product.active ?? true,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, product]);

  const update = <K extends keyof ProductForm>(key: K, value: ProductForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.name.trim()) {
      setError("El nombre del producto es obligatorio.");
      return;
    }
    if (toNumber(form.price) == null) {
      setError("El precio de venta es obligatorio.");
      return;
    }

    const payload = {
      name: form.name.trim(),
      sku: form.sku.trim() || null,
      barcode: form.barcode.trim() || null,
      model: form.model.trim() || null,
      category_id: form.category_id || null,
      brand_id: form.brand_id || null,
      image_url: form.image_url || null,
      internal_description: form.internal_description.trim() || null,
      catalog_description: form.catalog_description.trim() || null,
      cost_price: toNumber(form.cost_price),
      price: toNumber(form.price),
      wholesale_price: toNumber(form.wholesale_price),
      tax_rate: toNumber(form.tax_rate) ?? 0,
      unit: form.unit.trim() || "Unidad",
      min_stock: toNumber(form.min_stock) ?? 0,
      tire_size: form.tire_size.trim() || null,
      compatibility: form.compatibility.trim() || null,
      notes: form.notes.trim() || null,
      show_in_catalog: form.show_in_catalog,
      offer_active: form.offer_active,
      active: form.active,
      updated_at: new Date().toISOString(),
    };

    setSubmitting(true);
    try {
      if (product) {
        const { error: updateError } = await supabase
          .from("products")
          .update(payload)
          .eq("id", product.id);
        if (updateError) {
          setError("No se pudo actualizar el producto.");
          return;
        }
        await supabase
          .from("inventory")
          .update({ min_stock: payload.min_stock, unit: payload.unit, updated_at: new Date().toISOString() })
          .eq("product_id", product.id);

        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "editar",
          entity: "products",
          entityId: product.id,
          oldValue: { name: product.name, price: product.price },
          newValue: { name: payload.name, price: payload.price },
        });
        toast.success("Producto actualizado.");
      } else {
        const { data, error: insertError } = await supabase
          .from("products")
          .insert(payload)
          .select("id")
          .single();
        if (insertError || !data) {
          setError("No se pudo crear el producto.");
          return;
        }

        // Every new product becomes visible in inventory for all active branches.
        const { data: branches } = await supabase
          .from("branches")
          .select("id")
          .eq("active", true);
        const inventoryRows = (branches ?? []).map((branch) => ({
          product_id: data.id,
          branch_id: branch.id,
          stock: 0,
          min_stock: payload.min_stock,
          unit: payload.unit,
        }));
        if (inventoryRows.length > 0) {
          await supabase.from("inventory").insert(inventoryRows);
        }

        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "crear",
          entity: "products",
          entityId: data.id,
          branchId: profile?.branch_id ?? null,
          newValue: { name: payload.name, price: payload.price },
        });
        toast.success("Producto creado y agregado al inventario.");
      }
      onSaved();
      onClose();
    } catch {
      setError("Ocurrió un error inesperado. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={product ? "Editar producto" : "Nuevo producto"}
      subtitle="Completa la información del producto. Los campos se podrán editar después."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="product-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            {product ? "Guardar cambios" : "Crear producto"}
          </button>
        </>
      }
    >
      <form id="product-form" onSubmit={handleSubmit} className="flex flex-col gap-6">
        <section className="flex flex-col gap-4">
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Identificación</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Nombre del producto"
              name="name"
              value={form.name}
              onChange={(value) => update("name", value)}
              placeholder="Ej: Neumático 205/55 R16"
              required
            />
            <TextField
              label="Modelo"
              name="model"
              value={form.model}
              onChange={(value) => update("model", value)}
              placeholder="Modelo o referencia"
            />
            <TextField
              label="SKU"
              name="sku"
              value={form.sku}
              onChange={(value) => update("sku", value)}
              placeholder="Código interno"
            />
            <TextField
              label="Código de barras"
              name="barcode"
              value={form.barcode}
              onChange={(value) => update("barcode", value)}
              placeholder="Escanear o escribir"
            />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SelectField
              label="Categoría"
              name="category_id"
              value={form.category_id}
              onChange={(value) => update("category_id", value)}
              placeholder="Sin categoría"
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
            <SelectField
              label="Marca"
              name="brand_id"
              value={form.brand_id}
              onChange={(value) => update("brand_id", value)}
              placeholder="Sin marca"
              options={brands.map((b) => ({ value: b.id, label: b.name }))}
            />
          </div>
          <ImageUploader
            label="Imagen del producto"
            value={form.image_url || null}
            onChange={(url) => update("image_url", url)}
            folder="products"
            hint="Se usa en el catálogo y en el POS. Recomendado fondo claro y cuadrado."
            previewClassName="h-20 w-20"
          />
        </section>

        <section className="flex flex-col gap-4">
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Descripciones</h3>
          <TextAreaField
            label="Descripción interna"
            name="internal_description"
            value={form.internal_description}
            onChange={(value) => update("internal_description", value)}
            placeholder="Notas para uso interno del negocio."
            rows={2}
          />
          <TextAreaField
            label="Descripción para catálogo"
            name="catalog_description"
            value={form.catalog_description}
            onChange={(value) => update("catalog_description", value)}
            placeholder="Descripción que verán los clientes en el catálogo público."
            rows={2}
          />
        </section>

        <section className="flex flex-col gap-4">
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Precios</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <TextField
              label="Precio de venta"
              name="price"
              value={form.price}
              onChange={(value) => update("price", value)}
              inputMode="numeric"
              placeholder="0.00"
              required
            />
            <TextField
              label="Precio mayorista"
              name="wholesale_price"
              value={form.wholesale_price}
              onChange={(value) => update("wholesale_price", value)}
              inputMode="numeric"
              placeholder="0.00"
            />
            {canSeeCost ? (
              <TextField
                label="Precio de compra"
                name="cost_price"
                value={form.cost_price}
                onChange={(value) => update("cost_price", value)}
                inputMode="numeric"
                placeholder="0.00"
                hint="Solo visible para usuarios con permiso de costo."
              />
            ) : null}
            <TextField
              label="Impuesto (%)"
              name="tax_rate"
              value={form.tax_rate}
              onChange={(value) => update("tax_rate", value)}
              inputMode="numeric"
              placeholder="18"
            />
            <TextField
              label="Stock mínimo"
              name="min_stock"
              value={form.min_stock}
              onChange={(value) => update("min_stock", value)}
              inputMode="numeric"
              placeholder="0"
              hint="Genera alerta cuando el stock esté por debajo."
            />
            <TextField
              label="Unidad"
              name="unit"
              value={form.unit}
              onChange={(value) => update("unit", value)}
              placeholder="Unidad"
            />
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <h3 className="font-heading text-sm font-semibold text-foreground-900">
            Neumáticos y compatibilidad
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Medida"
              name="tire_size"
              value={form.tire_size}
              onChange={(value) => update("tire_size", value)}
              placeholder="Ej: 205/55 R16"
            />
            <TextField
              label="Compatibilidad"
              name="compatibility"
              value={form.compatibility}
              onChange={(value) => update("compatibility", value)}
              placeholder="Ej: Toyota Corolla 2015-2020"
            />
          </div>
          <TextAreaField
            label="Notas"
            name="notes"
            value={form.notes}
            onChange={(value) => update("notes", value)}
            placeholder="Información adicional del producto."
            rows={2}
          />
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Estado y catálogo</h3>
          <ToggleSwitch
            label="Producto activo"
            description="Los productos inactivos no se pueden vender en el POS."
            checked={form.active}
            onChange={(checked) => update("active", checked)}
          />
          <ToggleSwitch
            label="Mostrar en catálogo público"
            description="Aparece en /catalogo si cumple las reglas del catálogo."
            checked={form.show_in_catalog}
            onChange={(checked) => update("show_in_catalog", checked)}
          />
          <ToggleSwitch
            label="Oferta activa"
            description="Marca el producto como en oferta (los detalles se configuran en Ofertas)."
            checked={form.offer_active}
            onChange={(checked) => update("offer_active", checked)}
          />
        </section>

        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}